/**
 * Atom utility functions for building ARIA tree structure
 *
 * @package @alchemiesolns/svg-interactive
 */

import { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import { nameAtom } from '@alchemiesolns/feature-extractor';
import type { LonePairLeaf } from './types';

/**
 * Check if an atom is a hydrogen
 */
export function isHydrogen(workspace: MolecularWorkspace, atomId: string): boolean {
  const atom = workspace.getAtom(atomId);
  return atom?.symbol === 'H';
}

/**
 * Find all hydrogen atoms bonded to a given atom
 */
export function findBondedHydrogenIds(workspace: MolecularWorkspace, atomId: string): string[] {
  const hydrogenIds: string[] = [];
  for (const bond of workspace.getAllBonds()) {
    if (bond.a1.id === atomId && bond.a2.symbol === 'H') {
      hydrogenIds.push(bond.a2.id);
    } else if (bond.a2.id === atomId && bond.a1.symbol === 'H') {
      hydrogenIds.push(bond.a1.id);
    }
  }
  return hydrogenIds;
}

/**
 * Create lone pair leaf nodes for an atom
 *
 * When an atom has multiple lone pairs, we only create ONE entry in the tree
 * with the label "lone pair on {Element}". This simplifies the UI while still
 * allowing selection of the first lone pair for electron movement operations.
 *
 * @param atomTreeId - Tree ID of parent atom (may be feature-scoped)
 * @param atomSymbol - Element symbol for labeling
 * @param lonePairCount - Number of lone pairs (if > 0, creates exactly one node)
 * @param lonePairsById - Map to register lone pairs
 * @param parentById - Map to register parent relationships
 */
export function createLonePairNodes(
  atomTreeId: string,
  atomSymbol: string,
  lonePairCount: number,
  lonePairsById: Map<string, LonePairLeaf>,
  parentById: Map<string, string>
): LonePairLeaf[] {
  const nodes: LonePairLeaf[] = [];
  if (lonePairCount === 0) return nodes;

  // Extract original atomId from treeId (e.g., "feature-1:atom-1" -> "atom-1")
  const originalAtomId = atomTreeId.includes(':') ? atomTreeId.split(':')[1] : atomTreeId;
  // Use the bare element name (no charge/lone-pair/index modifiers) for the
  // "lone pair on {Element}" label.
  const name = nameAtom(atomSymbol);

  // Create only one lone pair node regardless of count
  // Always use -lp1 as the ID, label is always "lone pair on {Element}"
  const lonePairId = `${atomTreeId}-lp1`;
  const leaf: LonePairLeaf = {
    type: 'lonepair',
    id: lonePairId,
    lonePairId,
    atomId: originalAtomId,  // Original atom ID for SVG reference
    index: 1,
    label: `lone pair on ${name}`,
  };
  nodes.push(leaf);
  lonePairsById.set(lonePairId, leaf);
  parentById.set(lonePairId, atomTreeId);  // Parent is the atom's tree ID

  return nodes;
}
